<template>
    <div>
        <div class="vx-card p-8 sm:w-full md:w-2/5">
            <vs-input
                :label="$t('apps.registered-users.username')"
                type="text"
                class="w-full border-grey-light"
                v-model="internalUser.username"
                :required="true"
                :disabled="isEdit"
            />
            <div class="mt-1" v-if="getFieldErrors('username').length > 0">
                <p v-for="error in getFieldErrors('username')" class="m-0 text-danger text-xs" :key="error.message">{{ error.message }}</p>
            </div>

            <vs-input
                :label="$t('apps.registered-users.email')"
                type="email"
                class="mt-4 w-full border-grey-light"
                v-model="internalUser.email"
                :required="true"
            />
            <div class="mt-1" v-if="getFieldErrors('email').length > 0">
                <p v-for="error in getFieldErrors('email')" class="m-0 text-danger text-xs" :key="error.message">{{ error.message }}</p>
            </div>

            <button class="sk-btn sk-btn-primary rounded-lg mt-2" @click="handleResendRestorePasswordEmail">
                {{ $t('admin.users.actions.resend_restore_password_email') }}
            </button>

            <vs-input
                :label="$t('auth.login.input.password')"
                type="password"
                class="mt-4 w-full border-grey-light"
                v-model="internalUser.password"
                :required="true"
            />
            <vs-input
                type="password"
                class="mt-2 w-full border-grey-light"
                v-model="internalUser.password_confirmation"
                :required="true"
            />
            <div class="mt-1" v-if="getFieldErrors('password').length > 0">
                <p v-for="error in getFieldErrors('password')" class="m-0 text-danger text-xs" :key="error.message">{{ error.message }}</p>
            </div>

            <vs-select
                :label="$t('apps.bannerspaces.form.typeinput')"
                v-model="internalUser.type"
                class="mt-4 w-full border-grey-light"
                :disabled="isEdit"
            >
                <vs-select-item v-for="typeOption in typeOptions" :key="typeOption.key" :value="typeOption.key" :text="typeOption.value"/>
            </vs-select>

            <vs-checkbox
                v-if="internalUser.type === 'admin'"
                class="mt-4 w-full border-grey-light"
                v-model="internalUser.can_impersonate"
            > <p>{{ $t('admin.users.form.can_impersonate') }}</p>
            </vs-checkbox>

            <vs-input
                :label="$t('admin.users.form.firstname')"
                type="text"
                class="mt-2 w-full border-grey-light"
                v-model="internalUser.firstname"
                :required="true"
            />

            <vs-input
                :label="$t('admin.users.form.lastname')"
                type="text"
                class="mt-2 w-full border-grey-light"
                v-model="internalUser.lastname"
                :required="true"
            />

            <vs-select
                :label="$t('apps.registered-users.language')"
                v-model="internalUser.language"
                class="mt-4 w-full border-grey-light">
                <vs-select-item v-for="language in spotlioLanguages" :key="language.acronym" :value="language.acronym" :text="language.name"/>
            </vs-select>

            <Combobox
                class="mt-4"
                :label="$t('admin.users.form.managed_languages')"
                :options="spotlioLanguages.map(language => ({key: language.id, text: language.name}))"
                v-model="internalUser.managed_language_ids"
                :multiple="true"
            />

            <vs-select
                :label="$t('profile.label.timezone')"
                v-model="internalUser.timezone"
                class="mt-4 w-full border-grey-light">
                <vs-select-item v-for="timezone in timezones" :key="timezone" :value="timezone" :text="timezone"/>
            </vs-select>
        </div>

        <UserScopedRoles
            class="mt-8"
            :isAdmin="internalUser.type === 'admin'"
            :scopedRoles="internalUser.scoped_roles"
        />

        <div class="flex flex-row gap-4 mt-12">
            <button class="sk-btn sk-btn-primary rounded-lg btn-border" @click="handleCancel">
                {{ $t('apps.modals.cancel')}}
            </button>
            <button class="sk-btn sk-btn-primary rounded-lg" @click="handleSave" :disabled="formErrors.length > 0">
                {{ $t('Save')}}
            </button>
        </div>

    </div>
</template>

<script>

import timezones from '@/timezones.json'
import loader from "@/modules/Shared/Mixins/loader";
import {mapGetters} from "vuex";
import sha256 from "crypto-js/sha256";
import { v4 as uuidv4 } from 'uuid';
import UsersService from "@/modules/Admin/Services/UsersService";
import UserScopedRoles from "@/modules/Admin/Components/UserScopedRoles.vue";
import {parseUserScopedRolesToAPI} from "@/modules/Admin/Helpers/userScopedRolesHelper";
import AuthorizationService from "@/modules/Admin/Services/AuthorizationService";
import Combobox from "@/modules/Shared/Components/Combobox.vue";

export default {
  name: "UserForm",
  components: {Combobox, UserScopedRoles},
  mixins: [loader],
  props: {
      user: {
          type: Object,
          required: true,
      },
      isEdit: {
          type: Boolean,
          default: false
      }
  },
  data() {
      return {
          typeOptions: [
              { key: 'admin', value: 'Admin' },
              { key: 'editor', value: 'Editor' },
          ],
          timezones: timezones,
          internalUser: null,
          formErrors: [],
      }
  },
  created() {
      this.internalUser = {...this.user}
  },
  watch: {
      internalUser: {
          deep: true,
          handler: function () {
              if (this.internalUser.type !== 'admin') {
                  this.internalUser.can_impersonate = false
              }
              this.performValidatons()
          }
      }
  },
  computed: {
      ...mapGetters(['spotlioLanguages']),
  },
  methods: {
    getFieldErrors(field) {
        return this.formErrors.filter(error => error.field === field)
    },
    performValidatons() {
        this.formErrors = []

        if (this.internalUser.password !== this.internalUser.password_confirmation) {
            this.formErrors.push({ field: 'password', message: 'Passwords do not match' })
        }

        if (this.internalUser.password && this.internalUser.password.length < 8) {
            this.formErrors.push({ field: 'password', message: 'Password must be at least 8 characters' })
        }

        if (!this.isEdit) {
            if (!this.internalUser.password || !this.internalUser.password_confirmation) {
                this.formErrors.push({ field: 'password', message: 'Password is required' })
            }

            if (!this.internalUser.username) {
                this.formErrors.push({ field: 'username', message: 'Username is required' })
            }

            if (!this.internalUser.email) {
                this.formErrors.push({ field: 'email', message: 'Email is required' })
            }

            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.internalUser.email)) {
                this.formErrors.push({ field: 'email', message: 'Invalid email' })
            }
        }
    },
    handleCancel() {
        this.$router.push({ name: 'admin-users' });
    },
    async handleSave() {
        await this.loadAndNotify(async () => {
            let response;

            const userUuid = this.isEdit ? this.internalUser.uuid : sha256(uuidv4())

            const payload = {
                email: this.internalUser.email,
                can_impersonate: this.internalUser.can_impersonate,
                language: this.internalUser.language,
                timezone: this.internalUser.timezone,
                managed_language_ids: this.internalUser.managed_language_ids
            }

            if (this.internalUser.firstname) {
                payload.firstname = this.internalUser.firstname
            }

            if (this.internalUser.lastname) {
                payload.lastname = this.internalUser.lastname
            }

            if (this.internalUser.password) {
                payload.password = this.internalUser.password
                payload.password_confirmation = this.internalUser.password_confirmation
            }

            if (this.isEdit) {
                response = await UsersService.editUser(userUuid, payload)
            } else {
                payload.type = this.internalUser.type
                payload.username = this.internalUser.username

                response = await UsersService.createUser(userUuid, payload)
            }

            const userScopedRolesBody = parseUserScopedRolesToAPI(this.internalUser.scoped_roles)

            const editUserScopedRolesResponse = await AuthorizationService.editUserScopedRoles(userUuid, userScopedRolesBody)

            if (response.code !== 200 && editUserScopedRolesResponse.code !== 200) {
                return
            }

            await this.$router.push({ name: 'admin-users' });
        }, false)
    },
      async handleResendRestorePasswordEmail() {
          await this.loadAndNotify(async () => {
              await UsersService.sendEmail(this.$route.params.uuid, 'restore_password')
          })
      },
  }
};
</script>
